import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';

// Types
interface Notification {
  id: string;
  type: 'critical' | 'warning' | 'info' | 'success';
  title: string;
  description: string;
  timestamp: Date;
  read: boolean;
  icon?: string;
  actionUrl?: string;
}

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

interface NotificationCenterProps {
  onNavigate?: (page: string) => void;
}

// Toast Context (global state for toasts)
const toastQueue: Toast[] = [];
const toastListeners: ((toasts: Toast[]) => void)[] = [];

const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info', duration = 4000) => {
  const id = `toast-${Date.now()}-${Math.random()}`;
  const toast: Toast = { id, message, type, duration };
  toastQueue.push(toast);
  notifyToastListeners();

  if (duration > 0) {
    setTimeout(() => {
      removeToast(id);
    }, duration);
  }

  return id;
};

const removeToast = (id: string) => {
  const index = toastQueue.findIndex(t => t.id === id);
  if (index > -1) {
    toastQueue.splice(index, 1);
    notifyToastListeners();
  }
};

const notifyToastListeners = () => {
  toastListeners.forEach(listener => listener([...toastQueue]));
};

const subscribeToToasts = (listener: (toasts: Toast[]) => void) => {
  toastListeners.push(listener);
  return () => {
    const index = toastListeners.indexOf(listener);
    if (index > -1) {
      toastListeners.splice(index, 1);
    }
  };
};

// SVG Icons
const BellIcon = ({ size = 24, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const AlertCircleIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.8" />
    <text x="12" y="16" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">!</text>
  </svg>
);

const WarningIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

const CheckCircleIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

const InfoIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
);

const CloseIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
  </svg>
);

const EmptyStateIcon = ({ size = 80 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.4">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

// Utility Functions
const getRelativeTime = (date: Date): string => {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'critical':
      return <AlertCircleIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'success':
      return <CheckCircleIcon />;
    case 'info':
    default:
      return <InfoIcon />;
  }
};

const getTypeColor = (type: string): { bg: string; border: string; text: string } => {
  switch (type) {
    case 'critical':
      return { bg: '#FEE2E2', border: '#DC2626', text: '#991B1B' };
    case 'warning':
      return { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' };
    case 'success':
      return { bg: '#DCFCE7', border: '#16A34A', text: '#15803D' };
    case 'info':
    default:
      return { bg: '#DBEAFE', border: '#0284C7', text: '#0C2340' };
  }
};

// Auto-generate alerts from warehouse data
const generateAlertsFromWarehouseData = async (): Promise<Notification[]> => {
  const alerts: Notification[] = [];

  try {
    const data = await api.getAlertAll();
    if (Array.isArray(data)) {
      data.forEach((alert: any, idx: number) => {
        alerts.push({
          id: alert.id || `alert-${idx}`,
          type: alert.type || 'info',
          title: alert.title || 'Alert',
          description: alert.description || '',
          timestamp: alert.timestamp ? new Date(alert.timestamp) : new Date(),
          read: false,
          actionUrl: alert.actionUrl,
        });
      });
    }
  } catch (error) {
    console.error('Failed to fetch alerts:', error);
    // Fallback: try individual endpoints
    try {
      const expiryData = await api.getAlertExpiry();
      if (Array.isArray(expiryData)) {
        expiryData.forEach((item: any, idx: number) => {
          alerts.push({
            id: item.id || `expiry-${idx}`,
            type: item.type || 'warning',
            title: item.title || 'Expiry Alert',
            description: item.description || '',
            timestamp: new Date(),
            read: false,
          });
        });
      }
    } catch { /* silent */ }

    try {
      const capData = await api.getAlertCapacity();
      if (Array.isArray(capData)) {
        capData.forEach((item: any, idx: number) => {
          alerts.push({
            id: item.id || `cap-${idx}`,
            type: item.type || 'warning',
            title: item.title || 'Capacity Alert',
            description: item.description || '',
            timestamp: new Date(),
            read: false,
          });
        });
      }
    } catch { /* silent */ }
  }

  return alerts;
};

// NotificationCenter Component
const NotificationCenter: React.FC<NotificationCenterProps> = ({ onNavigate }) => {
  const { bgModeName } = useTheme();
  const isDark = bgModeName === 'dark' || bgModeName === 'midnight';
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch notifications from existing endpoint
      let apiAlerts: Notification[] = [];
      try {
        const notifData = await api.getNotifications();
        if (Array.isArray(notifData)) {
          apiAlerts = notifData.map((n: any) => ({
            id: String(n.id || Math.random()),
            type: n.type || 'info',
            title: n.title || 'Notification',
            description: n.message || n.description || '',
            timestamp: n.created_at ? new Date(n.created_at) : new Date(),
            read: n.read || false,
          }));
        }
      } catch { /* notifications endpoint may not exist */ }

      // Generate smart alerts from warehouse data
      const smartAlerts = await generateAlertsFromWarehouseData();

      // Merge and deduplicate
      const merged = [...apiAlerts, ...smartAlerts];
      const uniqueAlerts = Array.from(
        new Map(merged.map(alert => [alert.id, alert])).values()
      );

      setNotifications(uniqueAlerts);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      addToast('Failed to load notifications', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch and setup refresh interval
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAllAsRead = async () => {
    try {
      await api.markAllNotificationsRead();
    } catch { /* endpoint may not exist */ }
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
    addToast('All notifications marked as read', 'success');
  };

  const handleDismiss = async (id: string) => {
    // Optimistically remove from the panel + bump the unread counter
    // back down. Backend has no delete endpoint, but marking it read
    // means the next /auth/notifications poll won't re-add it to the
    // unread count. Was previously local-only — refreshing the page
    // brought the dismissed item back as still-unread.
    const prevList = notifications;
    setNotifications(prev => prev.filter(n => n.id !== id));
    addToast('Notification dismissed', 'success');
    try {
      const numericId = Number(id);
      if (Number.isFinite(numericId)) {
        await api.markNotificationRead(numericId);
      }
    } catch (error) {
      // Roll back the optimistic remove so the user sees what really happened.
      console.error('Failed to mark notification read:', error);
      setNotifications(prevList);
      addToast('Could not save dismiss — please try again', 'error');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      setNotifications(prev =>
        prev.map(n =>
          n.id === notification.id ? { ...n, read: true } : n
        )
      );
    }
    if (notification.actionUrl && onNavigate) {
      onNavigate(notification.actionUrl);
      setIsOpen(false);
    }
  };

  // Group notifications by type
  const groupedNotifications = {
    critical: notifications.filter(n => n.type === 'critical'),
    warning: notifications.filter(n => n.type === 'warning'),
    info: notifications.filter(n => n.type === 'info'),
    success: notifications.filter(n => n.type === 'success'),
  };

  const styles = {
    container: {
      position: 'relative' as const,
      display: 'inline-block',
    },
    button: {
      position: 'relative' as const,
      background: 'transparent',
      border: 'none',
      padding: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '8px',
      transition: 'background-color 0.2s ease',
      color: isDark ? '#E5E7EB' : '#1F2937',
    },
    buttonHover: {
      backgroundColor: isDark ? '#374151' : '#F3F4F6',
    },
    badge: {
      position: 'absolute' as const,
      top: '0px',
      right: '0px',
      backgroundColor: '#DC2626',
      color: 'white',
      borderRadius: '50%',
      width: '20px',
      height: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: 'bold' as const,
      border: `2px solid ${isDark ? '#1F2937' : '#FFFFFF'}`,
    },
    dropdown: {
      position: 'absolute' as const,
      top: '100%',
      right: '0px',
      marginTop: '8px',
      width: '380px',
      maxHeight: '480px',
      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
      borderRadius: '12px',
      boxShadow: isDark
        ? '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
        : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
      zIndex: 1000,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column' as const,
      animation: 'slideDown 0.3s ease-out',
    },
    header: {
      padding: '16px',
      borderBottom: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: isDark ? '#111827' : '#F9FAFB',
    },
    headerTitle: {
      fontSize: '16px',
      fontWeight: 'bold' as const,
      color: isDark ? '#F3F4F6' : '#1F2937',
    },
    markAllButton: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#0284C7',
      fontSize: '13px',
      cursor: 'pointer',
      fontWeight: '500' as const,
      padding: '4px 8px',
      borderRadius: '4px',
      transition: 'background-color 0.2s ease',
    },
    markAllButtonHover: {
      backgroundColor: isDark ? '#374151' : '#DBEAFE',
    },
    scrollArea: {
      overflowY: 'auto' as const,
      flex: 1,
      padding: '8px',
    },
    groupLabel: {
      fontSize: '12px',
      fontWeight: 'bold' as const,
      color: isDark ? '#9CA3AF' : '#6B7280',
      textTransform: 'uppercase' as const,
      padding: '12px 8px 8px 8px',
      marginTop: '8px',
    },
    notificationCard: (type: string) => ({
      padding: '12px',
      marginBottom: '8px',
      backgroundColor: isDark ? '#374151' : '#F9FAFB',
      borderRadius: '8px',
      borderLeft: `4px solid ${getTypeColor(type).border}`,
      cursor: 'pointer' as const,
      transition: 'all 0.2s ease',
      display: 'flex',
      gap: '12px',
    }),
    notificationCardHover: (type: string) => ({
      backgroundColor: isDark ? '#4B5563' : '#F3F4F6',
      transform: 'translateX(2px)',
    }),
    notificationIcon: (type: string) => ({
      color: getTypeColor(type).border,
      flexShrink: 0,
      marginTop: '2px',
    }),
    notificationContent: {
      flex: 1,
      minWidth: 0,
    },
    notificationTitle: {
      fontSize: '14px',
      fontWeight: '600' as const,
      color: isDark ? '#F3F4F6' : '#1F2937',
      marginBottom: '4px',
    },
    notificationDescription: {
      fontSize: '13px',
      color: isDark ? '#D1D5DB' : '#4B5563',
      marginBottom: '6px',
      lineHeight: '1.4',
    },
    notificationTimestamp: {
      fontSize: '12px',
      color: isDark ? '#9CA3AF' : '#9CA3AF',
    },
    dismissButton: {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: isDark ? '#9CA3AF' : '#9CA3AF',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      flexShrink: 0,
      transition: 'color 0.2s ease',
    },
    dismissButtonHover: {
      color: isDark ? '#F3F4F6' : '#1F2937',
    },
    footer: {
      padding: '12px 16px',
      borderTop: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
      textAlign: 'center' as const,
      backgroundColor: isDark ? '#111827' : '#F9FAFB',
    },
    viewAllLink: {
      color: '#0284C7',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500' as const,
      cursor: 'pointer',
      transition: 'color 0.2s ease',
    },
    viewAllLinkHover: {
      color: '#0369A1',
    },
    emptyState: {
      padding: '40px 20px',
      textAlign: 'center' as const,
      color: isDark ? '#9CA3AF' : '#6B7280',
    },
    emptyStateIcon: {
      marginBottom: '12px',
    },
    emptyStateText: {
      fontSize: '14px',
      marginBottom: '8px',
    },
    loadingContainer: {
      padding: '20px',
      textAlign: 'center' as const,
      color: isDark ? '#D1D5DB' : '#6B7280',
    },
  };

  const [buttonHovered, setButtonHovered] = useState(false);
  const [markAllHovered, setMarkAllHovered] = useState(false);
  const [hoveredNotificationId, setHoveredNotificationId] = useState<string | null>(null);
  const [hoveredDismissId, setHoveredDismissId] = useState<string | null>(null);
  const [viewAllHovered, setViewAllHovered] = useState(false);

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .notification-scroll::-webkit-scrollbar {
          width: 6px;
        }

        .notification-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .notification-scroll::-webkit-scrollbar-thumb {
          background: ${isDark ? '#4B5563' : '#D1D5DB'};
          border-radius: 3px;
        }

        .notification-scroll::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? '#6B7280' : '#9CA3AF'};
        }
      `}</style>

      <button
        ref={buttonRef}
        style={{
          ...styles.button,
          ...(buttonHovered ? styles.buttonHover : {}),
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setButtonHovered(true)}
        onMouseLeave={() => setButtonHovered(false)}
        aria-label="Notifications"
      >
        <BellIcon size={24} color={buttonHovered ? '#0284C7' : 'currentColor'} />
        {unreadCount > 0 && (
          <div style={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</div>
        )}
      </button>

      {isOpen && (
        <div ref={dropdownRef} style={styles.dropdown}>
          <div style={styles.header}>
            <span style={styles.headerTitle}>Notifications</span>
            {notifications.length > 0 && unreadCount > 0 && (
              <button
                style={{
                  ...styles.markAllButton,
                  ...(markAllHovered ? styles.markAllButtonHover : {}),
                }}
                onMouseEnter={() => setMarkAllHovered(true)}
                onMouseLeave={() => setMarkAllHovered(false)}
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          {isLoading ? (
            <div style={styles.loadingContainer}>Loading notifications...</div>
          ) : notifications.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>
                <EmptyStateIcon size={60} />
              </div>
              <div style={styles.emptyStateText}>No notifications</div>
              <div style={{ fontSize: '12px', color: isDark ? '#6B7280' : '#9CA3AF' }}>
                All caught up!
              </div>
            </div>
          ) : (
            <>
              <div style={styles.scrollArea} className="notification-scroll">
                {groupedNotifications.critical.length > 0 && (
                  <>
                    <div style={styles.groupLabel}>Critical</div>
                    {groupedNotifications.critical.map(notification => (
                      <div
                        key={notification.id}
                        style={{
                          ...styles.notificationCard(notification.type),
                          ...(hoveredNotificationId === notification.id
                            ? styles.notificationCardHover(notification.type)
                            : {}),
                        }}
                        onMouseEnter={() => setHoveredNotificationId(notification.id)}
                        onMouseLeave={() => setHoveredNotificationId(null)}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div style={styles.notificationIcon(notification.type)}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div style={styles.notificationContent}>
                          <div style={styles.notificationTitle}>{notification.title}</div>
                          <div style={styles.notificationDescription}>
                            {notification.description}
                          </div>
                          <div style={styles.notificationTimestamp}>
                            {getRelativeTime(notification.timestamp)}
                          </div>
                        </div>
                        <button
                          style={{
                            ...styles.dismissButton,
                            ...(hoveredDismissId === notification.id
                              ? styles.dismissButtonHover
                              : {}),
                          }}
                          onMouseEnter={() => setHoveredDismissId(notification.id)}
                          onMouseLeave={() => setHoveredDismissId(null)}
                          onClick={e => {
                            e.stopPropagation();
                            handleDismiss(notification.id);
                          }}
                          aria-label="Dismiss notification"
                        >
                          <CloseIcon size={16} />
                        </button>
                      </div>
                    ))}
                  </>
                )}

                {groupedNotifications.warning.length > 0 && (
                  <>
                    <div style={styles.groupLabel}>Warning</div>
                    {groupedNotifications.warning.map(notification => (
                      <div
                        key={notification.id}
                        style={{
                          ...styles.notificationCard(notification.type),
                          ...(hoveredNotificationId === notification.id
                            ? styles.notificationCardHover(notification.type)
                            : {}),
                        }}
                        onMouseEnter={() => setHoveredNotificationId(notification.id)}
                        onMouseLeave={() => setHoveredNotificationId(null)}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div style={styles.notificationIcon(notification.type)}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div style={styles.notificationContent}>
                          <div style={styles.notificationTitle}>{notification.title}</div>
                          <div style={styles.notificationDescription}>
                            {notification.description}
                          </div>
                          <div style={styles.notificationTimestamp}>
                            {getRelativeTime(notification.timestamp)}
                          </div>
                        </div>
                        <button
                          style={{
                            ...styles.dismissButton,
                            ...(hoveredDismissId === notification.id
                              ? styles.dismissButtonHover
                              : {}),
                          }}
                          onMouseEnter={() => setHoveredDismissId(notification.id)}
                          onMouseLeave={() => setHoveredDismissId(null)}
                          onClick={e => {
                            e.stopPropagation();
                            handleDismiss(notification.id);
                          }}
                          aria-label="Dismiss notification"
                        >
                          <CloseIcon size={16} />
                        </button>
                      </div>
                    ))}
                  </>
                )}

                {groupedNotifications.info.length > 0 && (
                  <>
                    <div style={styles.groupLabel}>Information</div>
                    {groupedNotifications.info.map(notification => (
                      <div
                        key={notification.id}
                        style={{
                          ...styles.notificationCard(notification.type),
                          ...(hoveredNotificationId === notification.id
                            ? styles.notificationCardHover(notification.type)
                            : {}),
                        }}
                        onMouseEnter={() => setHoveredNotificationId(notification.id)}
                        onMouseLeave={() => setHoveredNotificationId(null)}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div style={styles.notificationIcon(notification.type)}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div style={styles.notificationContent}>
                          <div style={styles.notificationTitle}>{notification.title}</div>
                          <div style={styles.notificationDescription}>
                            {notification.description}
                          </div>
                          <div style={styles.notificationTimestamp}>
                            {getRelativeTime(notification.timestamp)}
                          </div>
                        </div>
                        <button
                          style={{
                            ...styles.dismissButton,
                            ...(hoveredDismissId === notification.id
                              ? styles.dismissButtonHover
                              : {}),
                          }}
                          onMouseEnter={() => setHoveredDismissId(notification.id)}
                          onMouseLeave={() => setHoveredDismissId(null)}
                          onClick={e => {
                            e.stopPropagation();
                            handleDismiss(notification.id);
                          }}
                          aria-label="Dismiss notification"
                        >
                          <CloseIcon size={16} />
                        </button>
                      </div>
                    ))}
                  </>
                )}

                {groupedNotifications.success.length > 0 && (
                  <>
                    <div style={styles.groupLabel}>Success</div>
                    {groupedNotifications.success.map(notification => (
                      <div
                        key={notification.id}
                        style={{
                          ...styles.notificationCard(notification.type),
                          ...(hoveredNotificationId === notification.id
                            ? styles.notificationCardHover(notification.type)
                            : {}),
                        }}
                        onMouseEnter={() => setHoveredNotificationId(notification.id)}
                        onMouseLeave={() => setHoveredNotificationId(null)}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div style={styles.notificationIcon(notification.type)}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div style={styles.notificationContent}>
                          <div style={styles.notificationTitle}>{notification.title}</div>
                          <div style={styles.notificationDescription}>
                            {notification.description}
                          </div>
                          <div style={styles.notificationTimestamp}>
                            {getRelativeTime(notification.timestamp)}
                          </div>
                        </div>
                        <button
                          style={{
                            ...styles.dismissButton,
                            ...(hoveredDismissId === notification.id
                              ? styles.dismissButtonHover
                              : {}),
                          }}
                          onMouseEnter={() => setHoveredDismissId(notification.id)}
                          onMouseLeave={() => setHoveredDismissId(null)}
                          onClick={e => {
                            e.stopPropagation();
                            handleDismiss(notification.id);
                          }}
                          aria-label="Dismiss notification"
                        >
                          <CloseIcon size={16} />
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {notifications.length > 0 && (
                <div style={styles.footer}>
                  <a
                    href="#"
                    style={{
                      ...styles.viewAllLink,
                      ...(viewAllHovered ? styles.viewAllLinkHover : {}),
                    }}
                    onMouseEnter={() => setViewAllHovered(true)}
                    onMouseLeave={() => setViewAllHovered(false)}
                    onClick={e => {
                      e.preventDefault();
                      if (onNavigate) {
                        onNavigate('/notifications');
                        setIsOpen(false);
                      }
                    }}
                  >
                    View All Notifications
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// NotificationToast Component
interface NotificationToastProps {
  onClose?: () => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({ onClose }) => {
  const { bgModeName: toastBgMode } = useTheme();
  const isDark = toastBgMode === 'dark' || toastBgMode === 'midnight';
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsubscribe = subscribeToToasts(setToasts);
    return unsubscribe;
  }, []);

  const getToastColor = (type: string): { bg: string; border: string; text: string; icon: string } => {
    switch (type) {
      case 'error':
        return { bg: '#FEE2E2', border: '#DC2626', text: '#991B1B', icon: '#EF4444' };
      case 'warning':
        return { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E', icon: '#F59E0B' };
      case 'success':
        return { bg: '#DCFCE7', border: '#16A34A', text: '#15803D', icon: '#22C55E' };
      case 'info':
      default:
        return { bg: '#DBEAFE', border: '#0284C7', text: '#0C2340', icon: '#0284C7' };
    }
  };

  const styles = {
    container: {
      position: 'fixed' as const,
      top: '24px',
      right: '24px',
      zIndex: 2000,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
      pointerEvents: 'none' as const,
    },
    toast: (type: string) => {
      const color = getToastColor(type);
      return {
        backgroundColor: isDark ? '#1F2937' : color.bg,
        borderLeft: `4px solid ${color.border}`,
        borderRadius: '8px',
        padding: '16px',
        boxShadow: isDark
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        minWidth: '300px',
        maxWidth: '400px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        animation: 'slideInRight 0.3s ease-out',
        pointerEvents: 'auto' as const,
      };
    },
    toastIcon: (type: string) => ({
      color: getToastColor(type).icon,
      fontSize: '20px',
      flexShrink: 0,
    }),
    toastMessage: (type: string) => {
      const color = getToastColor(type);
      return {
        flex: 1,
        color: isDark ? '#E5E7EB' : color.text,
        fontSize: '14px',
        fontWeight: '500' as const,
      };
    },
    toastClose: (type: string) => ({
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: isDark ? '#9CA3AF' : getToastColor(type).text,
      opacity: 0.6,
      transition: 'opacity 0.2s ease',
    }),
  };

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <div style={styles.container}>
        {toasts.map(toast => (
          <div key={toast.id} style={styles.toast(toast.type)}>
            <span style={styles.toastIcon(toast.type)}>
              {toast.type === 'success' && '✓'}
              {toast.type === 'error' && '✕'}
              {toast.type === 'warning' && '!'}
              {toast.type === 'info' && 'ℹ'}
            </span>
            <span style={styles.toastMessage(toast.type)}>{toast.message}</span>
            <button
              style={styles.toastClose(toast.type)}
              onMouseEnter={e => {
                (e.target as HTMLElement).style.opacity = '1';
              }}
              onMouseLeave={e => {
                (e.target as HTMLElement).style.opacity = '0.6';
              }}
              onClick={() => removeToast(toast.id)}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </>
  );
};

// Export
export { NotificationCenter, NotificationToast, addToast, removeToast };
export default NotificationCenter;
